import { getRoot } from 'mobx-easy';
import { Run, Task } from '@bringg/types';

import RootStore from 'bringg-web/stores/root-store';
import { RoutesAction, RoutesActionsType } from '../stores/routes-monitoring-actions-store';
import { FETCH_DATA_INTERVAL } from './routes-monitoring-polling';

const isMatchIds = (id1: number, id2: number) => id1 === id2;

export const trailerMatch = (id1: number, id2: number) => isMatchIds(id1, id2);
export const vehicleMatch = (id1: number, id2: number) => isMatchIds(id1, id2);

const taskEventToCheck = (routeAction: RoutesAction, task: Task) => {
	switch (routeAction.action) {
		case RoutesActionsType.MOVE_TO_DISPATCH:
			return task.ready_to_execute === true;

		case RoutesActionsType.MOVE_TO_PLANNING:
			return task.ready_to_execute === false;

		case RoutesActionsType.DRIVER_ACTION:
			return task.user_id !== routeAction.actionParams.driverId || !task.run_id;

		case RoutesActionsType.VEHICLE_ACTION:
			return task.vehicle_id !== routeAction.actionParams.vehicleId;
	}
};

const routeEventToCheck = (routeAction: RoutesAction, run: Run) => {
	switch (routeAction.action) {
		case RoutesActionsType.MOVE_TO_DISPATCH:
			return run.ready_to_execute === true;

		case RoutesActionsType.MOVE_TO_PLANNING:
			return run.ready_to_execute === false;

		case RoutesActionsType.DRIVER_ACTION:
			return (
				run.user_id !== routeAction.actionParams.driverId ||
				(run.task_ids.length === routeAction.actionParams.startedTaskIds.length &&
					routeAction.actionParams.startedTaskIds.every(id => run.task_ids.includes(id)))
			);

		case RoutesActionsType.VEHICLE_ACTION:
			return run.vehicle_id !== routeAction.actionParams.vehicleId;
	}
};

const readyToExecuteUpdate = async (action: RoutesActionsType, tasksIds: number[]) => {
	switch (action) {
		case RoutesActionsType.MOVE_TO_DISPATCH:
			return getRoot<RootStore>().data.tasksStore.releaseToDrivers(tasksIds);

		case RoutesActionsType.MOVE_TO_PLANNING:
			return getRoot<RootStore>().data.tasksStore.moveToPlanning(tasksIds);
	}
};

const updateActionParams = (updatedParamsIds: number[], paramIdsSet: Set<number>) => {
	const param = updatedParamsIds.filter(paramId => paramIdsSet.has(paramId));
	return new Set(param);
};

const realtimeActionUpdate = (
	routeIds: number[],
	tasksIds: number[],
	routeAction: RoutesAction,
	waitForUpdate = false
) => {
	const unsubscribes = [];
	let rejectPromise: (reason?: any) => void;
	let updateEventParams: (routeIds: number[], tasksIds: number[]) => void;
	let _waitForUpdate = waitForUpdate;

	const timeout = setTimeout(() => {
		rejectPromise?.(`${routeAction} timeout`);
	}, FETCH_DATA_INTERVAL);

	let taskIdsSet = new Set(tasksIds);
	let routeIdsSet = new Set(routeIds);

	const areEventParamsResolved = () => taskIdsSet.size === 0 && routeIdsSet.size === 0;

	const updatePromise = new Promise<void>((resolve, reject) => {
		rejectPromise = reject;
		updateEventParams = (updatedRouteIds: number[], updatedTasksIds: number[]) => {
			taskIdsSet = updateActionParams(updatedTasksIds, taskIdsSet);
			routeIdsSet = updateActionParams(updatedRouteIds, routeIdsSet);

			if (areEventParamsResolved()) {
				resolve();
			} else {
				_waitForUpdate = false;
			}
		};

		unsubscribes.push(
			getRoot<RootStore>().data.tasksStore.onUpdate(taskEvent => {
				_waitForUpdate && taskIdsSet.add(taskEvent.current.id);

				if (taskIdsSet.has(taskEvent.current.id) && taskEventToCheck(routeAction, taskEvent.current)) {
					taskIdsSet.delete(taskEvent.current.id);
				}

				if (!_waitForUpdate && areEventParamsResolved()) {
					resolve();
				}
			})
		);

		unsubscribes.push(
			getRoot<RootStore>().data.runStore.onUpdate(data => {
				const run = data.current;
				_waitForUpdate && routeIdsSet.add(run.id);

				if (routeIdsSet.has(run.id) && routeEventToCheck(routeAction, run)) {
					routeIdsSet.delete(run.id);
				}

				if (!_waitForUpdate && areEventParamsResolved()) {
					resolve();
				}
			})
		);

		unsubscribes.push(
			getRoot<RootStore>().data.runStore.onDelete(data => {
				const run = data.diff;

				if (routeIdsSet.has(run.id)) {
					routeIdsSet.delete(run.id);
				}

				if (taskIdsSet.size === 0 && routeIdsSet.size === 0) {
					resolve();
				}
			})
		);
	}).finally(() => {
		unsubscribes.forEach(unsubscribe => {
			unsubscribe?.();
		});

		clearTimeout(timeout);
	});

	return { updatePromise, rejectPromise, updateEventParams };
};

export const routesMonitoringActionsProvider = {
	readyToExecuteUpdate,
	realtimeActionUpdate
};
